import {
  useState
} from 'react'
import {
  Clock,
  MapPin,
  Phone,
  Users,
  Video
} from 'lucide-react'
import type {
  LocationKind,
  PublicSchedulingLinkResponse
} from '../../client'
import {
  formatHostLine,
  getInitials,
  singleHostName
} from './schedulingUtils'

function LocationIcon({ kind }: { kind: LocationKind | null | undefined }): JSX.Element {
  const className = 'w-4 h-4 shrink-0 text-gray-400'
  if (kind === 'phone') return <Phone className={className} />
  if (kind === 'in_person') return <MapPin className={className} />
  return <Video className={className} />
}

// The host's profile photo when available, falling back to initials — both
// when there's no picture and when the picture URL fails to load (Google
// avatar URLs can expire / 404).
function HostAvatar({
  name,
  pictureUrl
}: {
  name: string,
  pictureUrl: string | null | undefined
}): JSX.Element {
  const [failed, setFailed] = useState(false)
  if (pictureUrl && !failed) {
    return (
      <img
        src={pictureUrl}
        alt={name}
        onError={() => setFailed(true)}
        className="w-12 h-12 rounded-full object-cover"
      />
    )
  }
  return (
    <div className="w-12 h-12 rounded-full bg-primary-500/10 text-primary-600 flex items-center justify-center text-base font-medium">
      {getInitials(name)}
    </div>
  )
}

export function EventInfoRail({
  link,
  className = '',
  children
}: {
  link: PublicSchedulingLinkResponse,
  className?: string,
  children?: React.ReactNode
}): JSX.Element {
  const hostLine = formatHostLine(link)
  const hostName = singleHostName(link)

  return (
    <div className={`p-6 flex flex-col gap-4 ${className}`}>
      <img src="/images/logo/symbol.svg" alt="Catch" className="h-6 w-auto self-start" />

      <div className="flex flex-col gap-3">
        {hostName && (
          <HostAvatar name={hostName} pictureUrl={link.owner_picture_url} />
        )}
        <div>
          {hostLine && <p className="text-sm text-gray-500">{hostLine}</p>}
          <h1 className="text-xl font-semibold text-gray-900 leading-tight mt-0.5">
            {link.title}
          </h1>
        </div>
      </div>

      <div className="flex flex-col gap-2 text-sm text-gray-600">
        <span className="flex items-center gap-2">
          <Clock className="w-4 h-4 shrink-0 text-gray-400" />
          {link.duration_minutes} min
        </span>
        {link.location_label && (
          <span className="flex items-center gap-2">
            <LocationIcon kind={link.location_kind} />
            {link.location_label}
          </span>
        )}
        {link.link_type === 'group' && link.group_capacity && (
          <span className="flex items-center gap-2">
            <Users className="w-4 h-4 shrink-0 text-gray-400" />
            Up to {link.group_capacity} per slot
          </span>
        )}
      </div>

      {link.description && (
        <p className="text-sm text-gray-500 whitespace-pre-wrap leading-relaxed">
          {link.description}
        </p>
      )}

      {children}
    </div>
  )
}
